/**
 * A live that repeats, as the person with a seat reads it (`prototype/
 * recurring-lives.html`, screens "Their seat link" to "After the last
 * session"): which session comes next, what already ran, what they did in
 * each, and the short words the seat link and the Room both say. Pure, so the
 * two pages always agree and it tests plainly.
 */
import { roomOpensAt } from "../../shared/live-time";
import type { WatchSeries, WatchSeriesSession, WatchSession, WatchSessionReplay } from "../../shared/types";

const MIN = 60_000;

/** The session to come: the one that is live, else the first still planned. None once it ended or they left. */
export function comingSession(series: WatchSeries): WatchSeriesSession | null {
  if (series.ended || series.left) return null;
  return series.sessions.find((s) => s.state === "live") ?? series.sessions.find((s) => s.state === "scheduled") ?? null;
}

/**
 * The session after the one at `seq`, for the seat link after a session. Why
 * not `comingSession`: until the page reads the room's end back, the session
 * that just ended still reads as live, and it must not count down to itself.
 */
export function sessionAfter(series: WatchSeries, seq: number): WatchSeriesSession | null {
  if (series.ended || series.left) return null;
  return series.sessions.find((s) => (s.state === "scheduled" || s.state === "live") && s.seq > seq) ?? null;
}

/** When the room of a session opens, in ms. */
export function opensAtMs(session: Pick<WatchSeriesSession, "startsAt">): number {
  return Date.parse(roomOpensAt(session.startsAt) ?? session.startsAt);
}

/** The rhythm alone, for a tab: the chip without its time. "Every Thu", "Every 2 weeks · Thu". */
export function rhythmWords(chip: string): string {
  const parts = chip.split(" · ");
  return parts.length > 1 ? parts.slice(0, -1).join(" · ") : chip;
}

/** "1 hour", "45 min", "1 hour 30 min". */
export function lengthWords(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h === 0) return `${m} min`;
  const hours = `${h} ${h === 1 ? "hour" : "hours"}`;
  return m > 0 ? `${hours} ${m} min` : hours;
}

/** What this person did with a session that ran, and what its replay offers them next. */
interface YouWords {
  /** They came live: said as a tag, not a line. */
  came: boolean;
  words: string;
  go: "Continue" | "Watch again" | "Watch the replay";
  /** How much of it is theirs, for the bar on the thumbnail. */
  pct: number;
}

export function youWords(s: Pick<WatchSeriesSession, "cameLive" | "stayedToEnd" | "watchedPct">): YouWords {
  const pct = Math.max(0, Math.min(100, Math.round(s.watchedPct)));
  if (s.cameLive && s.stayedToEnd) return { came: true, words: "You came live, to the end", go: "Watch again", pct: 100 };
  if (pct >= 100) return { came: s.cameLive, words: s.cameLive ? "You came live" : "You watched all of it", go: "Watch again", pct };
  const go = pct > 0 ? "Continue" : "Watch the replay";
  if (s.cameLive) return { came: true, words: "You came live", go, pct };
  return { came: false, words: pct > 0 ? `You watched ${pct}%` : "You missed this one", go, pct };
}

/** "Started 4 min ago", from when the host went live (unix sec). */
export function startedWords(wentLiveAt: number | null, nowMs: number): string {
  if (wentLiveAt === null) return "";
  const min = Math.max(0, Math.floor((nowMs - wentLiveAt * 1000) / MIN));
  return min < 1 ? "Started just now" : `Started ${min} min ago`;
}

/**
 * The talk as the player plays one session's replay: the webinar's own parts
 * (the host, the offer, the moments) with that session's picture, chapters
 * and this person's place in it.
 */
export function replayTalk(session: WatchSession, replay: WatchSessionReplay): WatchSession {
  return {
    ...session,
    playback_url: replay.playback_url,
    media_token: replay.media_token,
    poster_url: replay.poster_url,
    duration_sec: replay.duration_sec,
    chapters: replay.chapters,
    watched_sec: replay.watched_sec,
    watched_pct: replay.watched_pct,
  };
}

/** The series once they left it: what already ran, and nothing to come. */
export function afterLeaving(series: WatchSeries): WatchSeries {
  return {
    ...series,
    left: true,
    sessions: series.sessions.filter((s) => s.state === "ended" || s.state === "skipped" || s.state === "missed"),
  };
}
